import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AverageActionsForRelatedControlsResponse,
  AverageCostForRelatedControls,
  AverageSCScore,
  CountDoneActionsForRelatedControlsResponse,
  CountDoneActionsForSecurityControlResponse,
  CountInProgressActionsForSecurityControlResponse,
  CountOtherActionsForRelatedControlsResponse,
  CountOtherActionsForSecurityControlResponse,
  GlobalAverageActionsData,
  InProgressActionsForRelatedControlsResponse,
  LateRateForRelatedControls,
  RelatedControlsActionsCosts,
  RelatedControlsCostsResponse,
  RelatedControlsTree,
  SecurityControlData,
  SecurityControls,
  securityControlResponse,
} from '../../types/securityControls'
import {
  calculateAverageActionsForSecurityControl,
  calculateSecurityControlActionsCosts,
  fetchCountDoneActionsForRelatedControls,
  fetchCountDoneActionsForSecurityControl,
  fetchCountInProgressActionsForSecurityControl,
  fetchCountOtherActionsForRelatedControls,
  fetchCountOtherActionsForSecurityControl,
  getAllSecurity,
  getSecurityControlsWithScores,
  getSecurityISO,
  getSecurityNIST,
  updateSecuritycontrols,
  calculateAverageCostForSecurityControl,
  getSecurityControlById,
  fetchAuditsFromRelatedControls,
  fetchAverageSCScoreFromRelatedControls,
  fetchGlobalAverageActionsPerSecurityControl,
  fetchAverageActionsForRelatedControls,
  fetchAverageCostForRelatedControls,
  fetchRelatedControlsActionsCosts,
  fetchLateRateForRelatedControls,
  fetchRelatedControlsCosts,
  fetchInProgressActionsForRelatedControls,
  fetchRelatedControlsTreeByFamilyId,
  fetchSecurityControlsBySWIFT,
  fetchSecurityControlsBySAMA,
  fetchSecurityControlsByNIS2,
  fetchSecurityControlsByLabels,
} from './securityControlsAction'
import { SecurityCheckResponse } from '../../types/securityCheckFamilysTypes'
import { Audit } from '../../types/auditTypes'

interface SecurityControlState {
  SecurityControls: SecurityControls[]
  averageSCScore: AverageSCScore | null
  averageActionsData: AverageActionsForRelatedControlsResponse | null
  relatedAudits: Audit[]
  loading: boolean
  error: string | null
  SecurityControlsResponse: securityControlResponse | null
  updatedSecurity: SecurityControls | null
  data: SecurityCheckResponse['data']
  SecurityControlsActionsCosts?: { totalCost: number }
  AverageActionsForThisControl?: number
  securityControlData: SecurityControlData | null
  securityControl: SecurityControls | null
  dataAverage: GlobalAverageActionsData | null
  averageCostData: AverageCostForRelatedControls | null
  relatedControlsActionsCosts?: RelatedControlsActionsCosts | null
  lateRateForRelatedControls?: LateRateForRelatedControls | null
  relatedControlsCosts: RelatedControlsCostsResponse | null
  inProgressActions: InProgressActionsForRelatedControlsResponse
  relatedControlsTree: RelatedControlsTree | null
  relatedSecurityControls: SecurityControls[]
  countOtherActionsForSecurityControl: CountOtherActionsForSecurityControlResponse | null
  countOtherActionsForRelatedControls: CountOtherActionsForRelatedControlsResponse | null
  countInProgressActionsForSecurityControl: CountInProgressActionsForSecurityControlResponse | null
  countDoneActionsForSecurityControl: CountDoneActionsForSecurityControlResponse | null
  countDoneActionsForRelatedControls: CountDoneActionsForRelatedControlsResponse | null
}

const initialState: SecurityControlState = {
  inProgressActions: [],
  averageActionsData: null,
  SecurityControls: [],
  averageSCScore: null,
  relatedAudits: [],
  securityControl: null,
  loading: false,
  error: null,
  SecurityControlsResponse: null,
  updatedSecurity: null,
  data: [],
  SecurityControlsActionsCosts: undefined,
  AverageActionsForThisControl: undefined,
  securityControlData: null,
  dataAverage: null,
  averageCostData: null,
  relatedControlsActionsCosts: null,
  lateRateForRelatedControls: null,
  relatedControlsCosts: null,
  relatedControlsTree: null,
  relatedSecurityControls: [],
  // Initialize new state properties
  countOtherActionsForSecurityControl: null,
  countOtherActionsForRelatedControls: null,
  countInProgressActionsForSecurityControl: null,
  countDoneActionsForSecurityControl: null,
  countDoneActionsForRelatedControls: null,
}

export const securityControlsSlice = createSlice({
  name: 'securityCheckFamily',
  initialState,
  reducers: {
    clearRelatedData: (state) => {
      state.relatedSecurityControls = []
      state.securityControl = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSecurityISO.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityISO.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityControlsResponse = {
          securityControls: action.payload.securityControls,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getSecurityISO.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
    const addSourceCases = (thunk: any) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true
          state.error = null
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading = false
          state.SecurityControlsResponse = {
            securityControls: action.payload.response.securityControls ?? [],
            totalPages: action.payload.response.totalPages ?? 0,
            total: action.payload.response.total ?? 0,
            currentPage: action.payload.response.currentPage ?? 1,
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false
          state.error =
            (action.payload as string) || 'Failed to fetch security controls'
        })
    }

    addSourceCases(fetchSecurityControlsByNIS2)
    addSourceCases(fetchSecurityControlsBySAMA)
    addSourceCases(fetchSecurityControlsBySWIFT)
    builder
      .addCase(fetchInProgressActionsForRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchInProgressActionsForRelatedControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.inProgressActions = action.payload
        }
      )
      .addCase(
        fetchInProgressActionsForRelatedControls.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload ?? 'An error occurred'
        }
      )
      .addCase(fetchRelatedControlsCosts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRelatedControlsCosts.fulfilled, (state, action) => {
        state.loading = false
        state.relatedControlsCosts = action.payload
      })
      .addCase(fetchRelatedControlsCosts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
      .addCase(fetchRelatedControlsTreeByFamilyId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchRelatedControlsTreeByFamilyId.fulfilled,
        (state, action) => {
          state.loading = false
          state.relatedControlsTree = action.payload
        }
      )
      .addCase(fetchRelatedControlsTreeByFamilyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
      .addCase(fetchAverageActionsForRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAverageActionsForRelatedControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.averageActionsData = action.payload
        }
      )
      .addCase(
        fetchAverageActionsForRelatedControls.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload ?? 'An error occurred'
        }
      )
      .addCase(fetchGlobalAverageActionsPerSecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchGlobalAverageActionsPerSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.dataAverage = action.payload
        }
      )
      .addCase(
        fetchGlobalAverageActionsPerSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload || 'Something went wrong'
        }
      )
      .addCase(fetchAverageSCScoreFromRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAverageSCScoreFromRelatedControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.averageSCScore = action.payload
        }
      )
      .addCase(
        fetchAverageSCScoreFromRelatedControls.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload || 'Something went wrong'
        }
      )
      .addCase(fetchAuditsFromRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAuditsFromRelatedControls.fulfilled, (state, action) => {
        state.loading = false
        const audits = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.relatedAudits = audits
      })
      .addCase(fetchAuditsFromRelatedControls.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })
      .addCase(getSecurityControlById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityControlById.fulfilled, (state, action) => {
        state.loading = false
        state.securityControl = action.payload
      })
      .addCase(getSecurityControlById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Something went wrong'
      })
      .addCase(getSecurityNIST.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityNIST.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityControlsResponse = {
          securityControls: action.payload.securityControls,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getSecurityNIST.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getAllSecurity.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllSecurity.fulfilled, (state, action) => {
        state.loading = false
        state.SecurityControlsResponse = {
          securityControls: action.payload.securityControls,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getAllSecurity.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(updateSecuritycontrols.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSecuritycontrols.fulfilled, (state, action) => {
        state.loading = false
        state.updatedSecurity = action.payload
      })
      .addCase(updateSecuritycontrols.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getSecurityControlsWithScores.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getSecurityControlsWithScores.fulfilled,
        (state, action: PayloadAction<SecurityCheckResponse>) => {
          state.loading = false
          state.data = action.payload.data
        }
      )
      .addCase(getSecurityControlsWithScores.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'An error occurred'
      })
      .addCase(calculateSecurityControlActionsCosts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateSecurityControlActionsCosts.fulfilled,
        (state, action: PayloadAction<SecurityCheckResponse>) => {
          state.loading = false
          state.SecurityControlsActionsCosts = {
            totalCost: action.payload.totalCost,
          }
          state.data = action.payload.data
        }
      )
      .addCase(
        calculateSecurityControlActionsCosts.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload || 'An error occurred'
        }
      )
      .addCase(calculateAverageActionsForSecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateAverageActionsForSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.data = action.payload.data
          state.AverageActionsForThisControl = action.payload
            .AverageActionsForThisControl as number
        }
      )
      .addCase(
        calculateAverageActionsForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload || 'An error occurred'
        }
      )
      .addCase(calculateAverageCostForSecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateAverageCostForSecurityControl.fulfilled,
        (state, action: PayloadAction<SecurityControlData>) => {
          state.loading = false
          state.securityControlData = action.payload
        }
      )
      .addCase(
        calculateAverageCostForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.payload as string
        }
      )
      .addCase(fetchAverageCostForRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchAverageCostForRelatedControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.averageCostData = action.payload
        }
      )
      .addCase(fetchAverageCostForRelatedControls.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload ?? 'An error occurred while fetching average cost.'
      })
      .addCase(fetchRelatedControlsActionsCosts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchRelatedControlsActionsCosts.fulfilled, (state, action) => {
        state.loading = false
        state.relatedControlsActionsCosts = action.payload
      })
      .addCase(fetchRelatedControlsActionsCosts.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload ??
          'An error occurred while fetching related controls actions cost.'
      })
      .addCase(fetchLateRateForRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLateRateForRelatedControls.fulfilled, (state, action) => {
        state.loading = false
        state.lateRateForRelatedControls = action.payload
      })
      .addCase(fetchLateRateForRelatedControls.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'An error occurred'
      })
      .addCase(fetchSecurityControlsByLabels.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSecurityControlsByLabels.fulfilled, (state, action) => {
        state.loading = false
        state.relatedSecurityControls = action.payload
      })
      .addCase(fetchSecurityControlsByLabels.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload ?? 'Failed to fetch security controls by labels'
      })
      // New reducers for count APIs
      .addCase(fetchCountOtherActionsForSecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchCountOtherActionsForSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.countOtherActionsForSecurityControl = action.payload
        }
      )
      .addCase(
        fetchCountOtherActionsForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error =
            action.payload ??
            'Failed to count other actions for security control'
        }
      )
      .addCase(fetchCountOtherActionsForRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchCountOtherActionsForRelatedControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.countOtherActionsForRelatedControls = action.payload
        }
      )
      .addCase(
        fetchCountOtherActionsForRelatedControls.rejected,
        (state, action) => {
          state.loading = false
          state.error =
            action.payload ??
            'Failed to count other actions for related controls'
        }
      )
      .addCase(
        fetchCountInProgressActionsForSecurityControl.pending,
        (state) => {
          state.loading = true
          state.error = null
        }
      )
      .addCase(
        fetchCountInProgressActionsForSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.countInProgressActionsForSecurityControl = action.payload
        }
      )
      .addCase(
        fetchCountInProgressActionsForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error =
            action.payload ??
            'Failed to count in-progress actions for security control'
        }
      )
      .addCase(fetchCountDoneActionsForSecurityControl.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchCountDoneActionsForSecurityControl.fulfilled,
        (state, action) => {
          state.loading = false
          state.countDoneActionsForSecurityControl = action.payload
        }
      )
      .addCase(
        fetchCountDoneActionsForSecurityControl.rejected,
        (state, action) => {
          state.loading = false
          state.error =
            action.payload ??
            'Failed to count done actions for security control'
        }
      )
      .addCase(fetchCountDoneActionsForRelatedControls.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        fetchCountDoneActionsForRelatedControls.fulfilled,
        (state, action) => {
          state.loading = false
          state.countDoneActionsForRelatedControls = action.payload
        }
      )
      .addCase(
        fetchCountDoneActionsForRelatedControls.rejected,
        (state, action) => {
          state.loading = false
          state.error =
            action.payload ??
            'Failed to count done actions for related controls'
        }
      )
  },
})

export const { clearRelatedData } = securityControlsSlice.actions
export default securityControlsSlice.reducer
